import React, { useMemo, useRef } from "react";
import { Form, SelectProps } from "antd";
import { getRequiredRule } from "@/utils/formValidatorRules";
import { observer } from "mobx-react-lite";
import { MultiShopSelectPlatformType, MultiShopSelectPlatformTypeName } from "@/components/MultiShopSelect/types";
import MultiPlatformUseChildWaybillNoModeFormItem from "../MultiPlatformUseChildWaybillNoModeFormItem";
import WaybillPrinter from "@/services/print/waybill/WaybillPrinter";
import PrinterSelect from "@/components/PrinterSelect";
import OrderTag from "@/enum/OrderTag";
import "./index.less";
import TemplateSelectExtra from "../TemplateSelectExtra";
import { PlatformOrderInfo } from "..";
import { transformPlatformType2AliOutOrderTag } from "../utils";
import AliOutOrderWaybillTemplateFormItem from "./AliOutOrderWaybillTemplateFormItem";
import WaybillTemplateSelect, { WaybillTemplateSelectProps } from "@/components/WaybillTemplateSelect2";
import ShopType from "@/enum/ShopType";

interface WaybillTemplateFormItemProps {
    defaultTemplateId?: number;
    aliPlatformOrderInfo: PlatformOrderInfo;
}

const AliWaybillTemplateFormItem: React.FC<WaybillTemplateFormItemProps> = function (props) {
    const { defaultTemplateId, aliPlatformOrderInfo } = props;

    const showPrinterTipRef = useRef(true);

    const { hasNormal, onlyNormal, orderTags } = useMemo(() => {
        let _hasNormal = false;
        let _hasOutJd = false;

        const orderTags: OrderTag[] = [];

        Object.keys(aliPlatformOrderInfo).forEach((platform: MultiShopSelectPlatformType) => {
            if (!aliPlatformOrderInfo[platform]?.length) return;

            const orderTag = transformPlatformType2AliOutOrderTag(platform);

            switch (orderTag) {
                case OrderTag.ALI_OUT_ORDER_TAOBAO:
                case OrderTag.ALI_OUT_ORDER_PDD:
                case OrderTag.ALI_OUT_ORDER_DY:
                case OrderTag.ALI_OUT_ORDER_KWAI:
                case OrderTag.ALI_OUT_ORDER_XHS:
                case OrderTag.ALI_OUT_ORDER_JD:
                case OrderTag.ALI_OUT_ORDER_WEIXIN:
                case OrderTag.ALI_PLAINTEXT_OUT_ORDER_TAOBAO:
                case OrderTag.ALI_PLAINTEXT_OUT_ORDER_PDD:
                case OrderTag.ALI_PLAINTEXT_OUT_ORDER_DY:
                case OrderTag.ALI_PLAINTEXT_OUT_ORDER_KWAI:
                case OrderTag.ALI_PLAINTEXT_OUT_ORDER_XHS:
                case OrderTag.ALI_PLAINTEXT_OUT_ORDER_JD: {
                    orderTags.push(orderTag);

                    break;
                }

                default:
                    _hasNormal = true;
                    break;
            }
        });

        const onlyNormal = !orderTags.length;

        return {
            orderTags,
            hasNormal: _hasNormal,
            hasOutJd: _hasOutJd,
            onlyNormal,
        };
    }, [aliPlatformOrderInfo]);

    const templateSelectProps: WaybillTemplateSelectProps = {
        style: {
            width: 280,
        },
        defaultTemplateId: defaultTemplateId,
        onChange: (template) => {
            if (showPrinterTipRef.current) {
                if (template && !WaybillPrinter.isReady(template)) {
                    WaybillPrinter.openDownloadPluginDialog(template);
                    showPrinterTipRef.current = false;
                }
            }
        },
        popupMatchSelectWidth: false,
    };

    return (
        <Form.Item
            noStyle
            shouldUpdate={(prev, next) =>
                prev[MultiShopSelectPlatformType.ali]?.template !== next[MultiShopSelectPlatformType.ali]?.template ||
                prev.isGenerateWaybillNoOnly !== next.isGenerateWaybillNoOnly
            }
        >
            {(form) => {
                const values = form.getFieldsValue();

                const template = values[MultiShopSelectPlatformType.ali]?.template;
                const isGenerateWaybillNoOnly = values.isGenerateWaybillNoOnly;

                // 1.只有1688普通订单，显示的格式与其他平台的FormItem一致
                // 2.既有分销订单和有1688普通订单时，显示的格式与分销加密的FormItem一致

                if (onlyNormal) {
                    return (
                        <div className="multi-platform-print-waybill-form-item-container">
                            <div style={{ display: "flex", width: "100%" }}>
                                <Form.Item
                                    style={{
                                        flex: 1,
                                    }}
                                    label={`${
                                        MultiShopSelectPlatformTypeName[MultiShopSelectPlatformType.ali]
                                    }订单模板`}
                                    name={[MultiShopSelectPlatformType.ali, "template"]}
                                    rules={[getRequiredRule("快递单模板不能为空")]}
                                    labelCol={{
                                        span: 8,
                                    }}
                                    wrapperCol={{ span: 16 }}
                                    extra={
                                        <TemplateSelectExtra
                                            template={template}
                                            platform={MultiShopSelectPlatformType.ali}
                                        />
                                    }
                                >
                                    <WaybillTemplateSelect
                                        hideRefresh
                                        popupMatchSelectWidth={false}
                                        orderSourceCategory={MultiShopSelectPlatformType.ali}
                                        filterableShopType={ShopType.ALIBABA}
                                        {...templateSelectProps}
                                    />
                                </Form.Item>

                                <Form.Item
                                    style={{
                                        flex: 1,
                                    }}
                                    label="打印机"
                                    name={[MultiShopSelectPlatformType.ali, "printerName"]}
                                    rules={isGenerateWaybillNoOnly ? null : [getRequiredRule("打印机不能为空")]}
                                    labelCol={{
                                        span: 7,
                                    }}
                                    wrapperCol={{ span: 16 }}
                                >
                                    <PrinterSelect
                                        style={{ width: 240 }}
                                        disabled={isGenerateWaybillNoOnly}
                                        template={template}
                                    />
                                </Form.Item>
                            </div>

                            <MultiPlatformUseChildWaybillNoModeFormItem platform={MultiShopSelectPlatformType.ali} />
                        </div>
                    );
                }

                return (
                    <div className="multi-platform-print-waybill-form-item-container">
                        <Form.Item label="1688快递单模板">
                            <div className="ali-out-order-template-container">
                                {hasNormal && (
                                    <Form.Item colon={false} label=" ">
                                        <div style={{ display: "flex", width: "100%" }}>
                                            <Form.Item
                                                className="out-order-form-item"
                                                name={[MultiShopSelectPlatformType.ali, "template"]}
                                                label="1688普通订单模板"
                                                rules={[{ required: true }]}
                                                extra={
                                                    <TemplateSelectExtra
                                                        template={template}
                                                        platform={MultiShopSelectPlatformType.ali}
                                                    />
                                                }
                                            >
                                                <WaybillTemplateSelect
                                                    orderSourceCategory={MultiShopSelectPlatformType.ali}
                                                    {...templateSelectProps}
                                                    filterableShopType={ShopType.ALIBABA}
                                                />
                                            </Form.Item>

                                            <Form.Item
                                                name={[MultiShopSelectPlatformType.ali, "printerName"]}
                                                rules={
                                                    isGenerateWaybillNoOnly ? null : [getRequiredRule("打印机不能为空")]
                                                }
                                            >
                                                <PrinterSelect
                                                    style={{ width: 200 }}
                                                    className="ml10"
                                                    disabled={isGenerateWaybillNoOnly}
                                                    template={template}
                                                />
                                            </Form.Item>
                                        </div>

                                        <MultiPlatformUseChildWaybillNoModeFormItem
                                            platform={MultiShopSelectPlatformType.ali}
                                        />
                                    </Form.Item>
                                )}

                                {orderTags.map((orderTag) => {
                                    return (
                                        <AliOutOrderWaybillTemplateFormItem
                                            key={orderTag}
                                            tag={orderTag}
                                            templateSelectProps={templateSelectProps}
                                        />
                                    );
                                })}
                            </div>
                        </Form.Item>
                    </div>
                );
            }}
        </Form.Item>
    );
};

export default observer(AliWaybillTemplateFormItem);
